import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import GenericModal from "../genericModal/GenericModal";
import DateInput from "../../ui/DateInput";
import "./EditableModal.css";

const EditableModal = ({
  show,
  onHide,
  title,
  initialData,
  fields,
  onSave,
  closeOnSave = true,
  onValidationError,
  validate,
  langSwitch = null,
  size,
}) => {
  const { t } = useTranslation("layout");
  const [formData, setFormData] = useState({});
  const [validationError, setValidationError] = useState("");

  // Смена языка попапа пересобирает fields (меняются подписи), но набор
  // имён полей остаётся прежним — по нему решаем, надо ли сбрасывать форму.
  const fieldNamesKey = fields.map((field) => field.name).join("|");

  useEffect(() => {
    if (show && initialData) {
      const initialValues = {};
      fields.forEach((field) => {
        initialValues[field.name] = initialData[field.name] ?? "";
      });
      setFormData(initialValues);
      setValidationError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialData, fieldNamesKey]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getMissingRequiredFields = () =>
    fields.filter((field) => {
      if (!field.required) return false;
      const value = formData[field.name];
      if (field.type === "file" || field.type === "checkboxes") {
        return !Array.isArray(value) || value.length === 0;
      }

      return String(value ?? "").trim() === "";
    });

  const handleSave = async () => {
    const missingFields = getMissingRequiredFields();
    if (missingFields.length > 0) {
      const errorText = t("editableModal.requiredFields", {
        fields: missingFields.map((field) => field.label).join(", "),
      });
      setValidationError(errorText);
      if (typeof onValidationError === "function") {
        onValidationError(missingFields);
      }
      return;
    }

    setValidationError("");

    // Дополнительная проверка формы (например, «название хотя бы на одном
    // языке»): возвращает текст ошибки или пустое значение.
    if (typeof validate === "function") {
      const message = validate(formData);
      if (message) {
        setValidationError(message);
        return;
      }
    }

    const result = await onSave(formData);
    if (closeOnSave && result !== false) {
      onHide();
    }
  };

  // Поля с `lang` показываем только для выбранного языка попапа.
  const visibleFields = langSwitch
    ? fields.filter((field) => !field.lang || field.lang === langSwitch.value)
    : fields;

  return (
    <GenericModal
      show={show}
      onHide={onHide}
      title={title}
      onConfirm={handleSave}
      confirmText={t("editableModal.confirm")}
      size={size}>
      {langSwitch && Array.isArray(langSwitch.options) && langSwitch.options.length > 0 ? (
        <div className="editable-modal-lang" role="group">
          {langSwitch.options.map((option) => (
            <button
              type="button"
              key={option.value}
              className={`editable-modal-lang__btn${
                option.value === langSwitch.value ? " is-active" : ""
              }`}
              onClick={() => langSwitch.onChange?.(option.value)}>
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="editable-modal-grid">
        {visibleFields.map((field) => {
        const {
          name,
          label,
          type = "text",
          readOnly = false,
          multiple = false,
          required = false,
          requiredMark = false,
          rows = 7,
          span = 12,
        } = field;
        const value = formData[name] ?? "";
        const cellClass = "mb-3 editable-modal-cell";
        const cellStyle = span !== 12 ? { "--editable-span": span } : undefined;
        const requiredLabel = required || requiredMark ? " *" : "";

        if (type === "textarea") {
          return (
            <Form.Group className={cellClass} style={cellStyle} key={name}>
              <Form.Label>
                {label}
                {requiredLabel}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={rows}
                value={value}
                onChange={(e) => handleChange(name, e.target.value)}
                readOnly={readOnly}
                required={required}
                placeholder={t("editableModal.fieldPlaceholder", { label })}
              />
            </Form.Group>
          );
        }

        if (type === "file") {
          const selectedFiles = Array.isArray(value) ? value : [];
          return (
            <Form.Group className={cellClass} style={cellStyle} key={name}>
              <Form.Label>
                {label}
                {requiredLabel}
              </Form.Label>
              <Form.Control
                type="file"
                onChange={(e) =>
                  handleChange(name, Array.from(e.target.files ?? []))
                }
                readOnly={readOnly}
                multiple={multiple}
                required={required}
              />
              {selectedFiles.length > 0 && (
                <div className="mt-2 small text-muted">
                  {t("editableModal.filesSelected", { count: selectedFiles.length })}
                </div>
              )}
            </Form.Group>
          );
        }

        if (type === "select") {
          const options = Array.isArray(field.options) ? field.options : [];
          return (
            <Form.Group className={cellClass} style={cellStyle} key={name}>
              <Form.Label>
                {label}
                {requiredLabel}
              </Form.Label>
              <Form.Select
                value={value}
                onChange={(e) => handleChange(name, e.target.value)}
                disabled={readOnly}
                required={required}>
                <option value="">{t("editableModal.selectPlaceholder")}</option>
                {options.map((option) => {
                  const optionValue =
                    option && typeof option === "object"
                      ? option.value
                      : option;
                  const optionLabel =
                    option && typeof option === "object"
                      ? (option.label ?? option.value)
                      : option;
                  return (
                    <option key={String(optionValue)} value={optionValue}>
                      {optionLabel}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          );
        }

        if (type === "checkboxes") {
          const options = Array.isArray(field.options) ? field.options : [];
          const selected = Array.isArray(value)
            ? value.map((item) => String(item))
            : [];
          const toggleOption = (optionValue) => {
            const normalized = String(optionValue);
            const next = selected.includes(normalized)
              ? selected.filter((item) => item !== normalized)
              : [...selected, normalized];
            handleChange(name, next);
          };
          return (
            <Form.Group className={cellClass} style={cellStyle} key={name}>
              <Form.Label>
                {label}
                {requiredLabel}
              </Form.Label>
              {options.length === 0 ? (
                <div className="editable-modal-checkboxes__empty">
                  {t("editableModal.listEmpty")}
                </div>
              ) : (
                <div className="editable-modal-checkboxes">
                  {options.map((option) => {
                    const optionValue =
                      option && typeof option === "object"
                        ? option.value
                        : option;
                    const optionLabel =
                      option && typeof option === "object"
                        ? (option.label ?? option.value)
                        : option;
                    return (
                      <Form.Check
                        key={String(optionValue)}
                        type="checkbox"
                        id={`${name}-${optionValue}`}
                        label={optionLabel}
                        checked={selected.includes(String(optionValue))}
                        onChange={() => toggleOption(optionValue)}
                        disabled={readOnly}
                      />
                    );
                  })}
                </div>
              )}
              {field.hint ? (
                <div className="editable-modal-checkboxes__hint">
                  {field.hint}
                </div>
              ) : null}
            </Form.Group>
          );
        }

        if (type === "date") {
          return (
            <Form.Group className={cellClass} style={cellStyle} key={name}>
              <Form.Label>
                {label}
                {requiredLabel}
              </Form.Label>
              <DateInput
                className="form-control"
                value={value}
                onChange={(e) => handleChange(name, e.target.value)}
                readOnly={readOnly}
                required={required}
              />
            </Form.Group>
          );
        }

        return (
          <Form.Group className={cellClass} style={cellStyle} key={name}>
            <Form.Label>
              {label}
              {requiredLabel}
            </Form.Label>
            <Form.Control
              type={type}
              value={value}
              onChange={(e) => handleChange(name, e.target.value)}
              readOnly={readOnly}
              required={required}
            />
          </Form.Group>
        );
        })}
      </div>
      {validationError ? (
        <div className="text-danger small mt-2">{validationError}</div>
      ) : null}
    </GenericModal>
  );
};

export default EditableModal;
